/**
 * Error handling utilities for type-safe error handling in catch blocks
 */

import { ApiError, type ApiErrorResponse } from '../types/api-common';
import { AxiosError } from 'axios';

interface ErrorMessageOptions {
  includeRequestId?: boolean;
}

type HeadersLike = Headers | Record<string, unknown> | undefined | null;

interface RequestIdCarrier {
  requestId?: string;
}

function getDetailMessage(detail: unknown): string | undefined {
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail.trim();
  }

  if (Array.isArray(detail) && detail.length > 0) {
    for (const entry of detail) {
      if (typeof entry === 'string' && entry.trim().length > 0) {
        return entry.trim();
      }
      if (entry && typeof entry === 'object' && typeof (entry as { msg?: unknown }).msg === 'string') {
        const message = ((entry as { msg: string }).msg || '').trim();
        if (message) return message;
      }
    }
  }

  if (detail && typeof detail === 'object') {
    const candidate = detail as { message?: unknown; detail?: unknown; error?: unknown };
    return (
      getDetailMessage(candidate.message)
      || getDetailMessage(candidate.detail)
      || getDetailMessage(candidate.error)
    );
  }

  return undefined;
}

function getRequestIdFromHeaders(headers: HeadersLike): string | undefined {
  if (!headers) return undefined;

  if (typeof (headers as { get?: unknown }).get === 'function') {
    const headerValue = (headers as Headers).get('x-request-id') ?? (headers as Headers).get('X-Request-Id');
    return typeof headerValue === 'string' && headerValue.trim().length > 0 ? headerValue.trim() : undefined;
  }

  if (typeof headers === 'object') {
    for (const [key, value] of Object.entries(headers)) {
      if (key.toLowerCase() !== 'x-request-id') continue;
      if (Array.isArray(value)) {
        const candidate = value.find((entry) => typeof entry === 'string' && entry.trim().length > 0);
        if (typeof candidate === 'string') return candidate.trim();
        continue;
      }
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
  }

  return undefined;
}

function shouldExposeRequestId(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (window.localStorage.getItem('debug') === 'true') {
      return true;
    }
  } catch {
    // Ignore blocked storage; the environment and hostname checks still apply.
  }

  if (import.meta.env.MODE === 'development') {
    return true;
  }

  return /\.vercel\.app$/i.test(window.location.hostname || '');
}

function withRequestId(message: string, requestId?: string, includeRequestId = shouldExposeRequestId()): string {
  if (!includeRequestId || !requestId || message.includes(requestId)) {
    return message;
  }
  return `${message} (Reference ID: ${requestId})`;
}

function withRetryDelay(message: string, status?: number, seconds?: number): string {
  if (status !== 429 || !Number.isInteger(seconds) || !seconds || seconds < 0) return message;
  const suffix = `Try again in ${seconds} seconds.`;
  return message.endsWith(suffix) ? message : `${message} ${suffix}`;
}

export function withErrorRequestId(message: string, error: unknown, options?: ErrorMessageOptions): string {
  const includeRequestId = options?.includeRequestId ?? shouldExposeRequestId();
  return withRequestId(message, getErrorRequestId(error), includeRequestId);
}

function getAxiosResponsePayload(error: AxiosError): ApiErrorResponse | Record<string, unknown> | undefined {
  const payload = error.response?.data;
  return payload && typeof payload === 'object' ? payload as ApiErrorResponse | Record<string, unknown> : undefined;
}

export function getErrorRequestId(error: unknown): string | undefined {
  if (isApiError(error)) {
    return error.requestId;
  }

  if (isAxiosError(error)) {
    return (
      getRequestIdFromHeaders(error.response?.headers as HeadersLike)
      || (error as AxiosError & RequestIdCarrier).requestId
    );
  }

  if (isError(error) && 'requestId' in error) {
    const requestId = (error as Error & RequestIdCarrier).requestId;
    return typeof requestId === 'string' && requestId.trim().length > 0 ? requestId.trim() : undefined;
  }

  if (typeof error === 'object' && error !== null && 'requestId' in error) {
    const requestId = (error as RequestIdCarrier).requestId;
    return typeof requestId === 'string' && requestId.trim().length > 0 ? requestId.trim() : undefined;
  }

  return undefined;
}

export function createApiError(message: string, options?: {
  status?: number;
  detail?: string;
  errors?: Record<string, string[]>;
  requestId?: string;
  cause?: unknown;
  code?: string;
  retryAfterSeconds?: number;
}): ApiError {
  const retryAfter = options?.retryAfterSeconds;
  const validRetryAfter = typeof retryAfter === 'number' && Number.isInteger(retryAfter) && retryAfter > 0
    ? retryAfter : undefined;
  const apiError = new ApiError(
    withRetryDelay(message, options?.status, validRetryAfter),
    options?.status,
    options?.detail,
    options?.errors,
    options?.requestId,
  );
  apiError.code = options?.code;
  apiError.retryAfterSeconds = validRetryAfter;
  if (options?.cause !== undefined) {
    (apiError as Error & { cause?: unknown }).cause = options.cause;
  }
  return apiError;
}

export async function createFetchApiError(
  response: Response,
  fallbackMessage?: string,
): Promise<ApiError> {
  const requestId = getRequestIdFromHeaders(response.headers);
  let text = '';

  try {
    text = await response.text();
  } catch {
    text = '';
  }

  let parsedBody: unknown;
  if (text) {
    try {
      parsedBody = JSON.parse(text);
    } catch {
      parsedBody = undefined;
    }
  }

  const parsedObject = parsedBody && typeof parsedBody === 'object'
    ? parsedBody as ApiErrorResponse | Record<string, unknown>
    : undefined;

  const detail = getDetailMessage(parsedObject?.detail) || getDetailMessage(parsedObject?.error);
  const errors = parsedObject && 'errors' in parsedObject && typeof parsedObject.errors === 'object'
    ? parsedObject.errors as Record<string, string[]>
    : undefined;
  const message = detail || (text.trim().length > 0 ? text.trim() : undefined) || fallbackMessage || `HTTP error! status: ${response.status}`;

  return createApiError(message, {
    status: response.status,
    code: payloadCode(parsedObject),
    detail,
    errors,
    requestId,
    retryAfterSeconds: Number(response.headers?.get('Retry-After')),
  });
}

/**
 * Type guard to check if error is an Error instance
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Type guard to check if error is an AxiosError
 */
export function isAxiosError<TData = unknown>(
  error: unknown,
): error is AxiosError<TData> {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Type guard to check if error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function getErrorStatus(error: unknown): number | undefined {
  return isApiError(error) ? error.status : isAxiosError(error) ? error.response?.status : undefined;
}

function payloadCode(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const value = payload as { code?: unknown; detail?: unknown };
  if (typeof value.code === 'string') return value.code;
  if (value.detail && typeof value.detail === 'object' && 'code' in value.detail
    && typeof value.detail.code === 'string') return value.detail.code;
  return undefined;
}

export function getErrorCode(error: unknown): string | undefined {
  return isApiError(error) ? error.code : isAxiosError(error) ? payloadCode(error.response?.data) : undefined;
}

/**
 * Extract a user-friendly error message from any error type
 */
export function getErrorMessage(error: unknown, options?: ErrorMessageOptions): string {
  const includeRequestId = options?.includeRequestId ?? shouldExposeRequestId();

  if (isApiError(error)) {
    return withRequestId(withRetryDelay(error.detail || error.message, error.status, error.retryAfterSeconds), error.requestId, includeRequestId);
  }

  if (isAxiosError(error)) {
    const requestId = getErrorRequestId(error);
    const payload = getAxiosResponsePayload(error);
    const message = getDetailMessage(payload?.detail) || getDetailMessage(payload?.error) || error.message;
    const headers = error.response?.headers;
    const retryHeader = headers && Object.entries(headers).find(([key]) => key.toLowerCase() === 'retry-after')?.[1];
    return withRequestId(withRetryDelay(message, error.response?.status, Number(retryHeader)), requestId, includeRequestId);
  }

  if (isError(error)) {
    return withRequestId(error.message, getErrorRequestId(error), includeRequestId);
  }

  if (typeof error === 'string') {
    return withRequestId(error, undefined, includeRequestId);
  }

  return 'An unexpected error occurred';
}

const SHOT_ASSET_ERROR_PREFIX = /^(?:shot generation failed|shot edit failed|image generation failed)\s*:\s*/i;
const SHOT_ASSET_BLOCKED_PATTERNS = [
  /generation blocked/i,
  /prohibited_content/i,
  /blocklist/i,
  /\bsafety\b/i,
  /safety filter/i,
  /content was blocked/i,
];

function stripShotAssetErrorPrefixes(message: string): string {
  let normalized = message.trim();

  while (SHOT_ASSET_ERROR_PREFIX.test(normalized)) {
    normalized = normalized.replace(SHOT_ASSET_ERROR_PREFIX, '').trim();
  }

  return normalized;
}

function isShotAssetBlockedMessage(message: string): boolean {
  return SHOT_ASSET_BLOCKED_PATTERNS.some((pattern) => pattern.test(message));
}

export interface ShotAssetFailureDisplay {
  title: string;
  detail: string;
  severity: 'warning' | 'error';
}

export function getShotAssetFailureDisplay(reason?: string | null): ShotAssetFailureDisplay | null {
  if (typeof reason !== 'string' || reason.trim().length === 0) {
    return null;
  }

  const detail = stripShotAssetErrorPrefixes(reason);
  if (detail.length === 0) {
    return null;
  }

  if (isShotAssetBlockedMessage(detail)) {
    return {
      title: 'Safety/content policy',
      detail,
      severity: 'warning',
    };
  }

  if (/panel split produced no images/i.test(detail)) {
    return {
      title: 'Image processing error',
      detail,
      severity: 'error',
    };
  }

  return {
    title: 'Technical generation error',
    detail,
    severity: 'error',
  };
}

export function formatShotAssetFailureReason(reason?: string | null): string | null {
  const failure = getShotAssetFailureDisplay(reason);
  if (!failure) {
    return null;
  }

  if (failure.title === 'Safety/content policy') {
    return 'Blocked by the image provider content policy. Try softening graphic or explicit details, removing sensitive references, or changing the prompt or visual style.';
  }

  if (failure.title === 'Image processing error') {
    return 'The generated image could not be split into the selected panel layout. Try a different layout and run it again.';
  }

  return failure.detail;
}

export function formatShotAssetActionError(error: unknown, fallback: string): string {
  const detail = getErrorMessage(error, { includeRequestId: false });
  return formatShotAssetFailureReason(detail) || fallback;
}

/**
 * Log error with proper typing
 */
export function logError(error: unknown, context?: string): void {
  const message = getErrorMessage(error);
  const requestId = getErrorRequestId(error);
  const prefix = context ? `[${context}]` : '';

  if (isError(error)) {
    console.error(`${prefix} Error:`, message, requestId ? { requestId } : undefined, error);
  } else {
    console.error(`${prefix} Error:`, message, requestId ? { requestId } : undefined);
  }
}

/**
 * Type guard for objects with message property
 */
export function hasMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  );
}

/**
 * Type guard for objects with status code
 */
export function hasStatusCode(error: unknown): error is { status: number } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

/**
 * Convert unknown error to Error instance
 */
export function toError(error: unknown): Error {
  if (isError(error)) {
    return error;
  }

  if (hasMessage(error)) {
    return new Error(error.message);
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  return new Error('An unexpected error occurred');
}
